import React, { useEffect, useState } from 'react';
import { doctoralApi } from '@/features/workspace/api';
import { DoctoralInternshipData } from '../types';
import { OFFICIAL } from '../doctoralTemplate';
import { Card, DocRow, Field, Hero, Kpis, SourceCard } from './dp120Parts';

interface DoctoralInternshipViewProps {
  data: DoctoralInternshipData | null;
  onUploadDoc?: (docType: string, file: File) => void;
}

export const DoctoralInternshipView: React.FC<DoctoralInternshipViewProps> = ({ data, onUploadDoc }) => {
  const [form, setForm] = useState<DoctoralInternshipData>(
    data || { status: 'PLANNING' },
  );
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const set = (key: keyof DoctoralInternshipData) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const text = (key: keyof DoctoralInternshipData) => String(form[key] ?? '');

  const save = async () => {
    setNotice('');
    try {
      await doctoralApi.updateInternship(form);
      setNotice('Стажировка сохранена.');
    } catch {
      setNotice('Не удалось сохранить стажировку.');
    }
  };

  const pick = (key: keyof DoctoralInternshipData) => async (file: File) => {
    onUploadDoc?.(String(key), file);
    const next = { ...form, [key]: file.name };
    setForm(next);
    try {
      await doctoralApi.updateInternship(next);
      setNotice(`Файл «${file.name}» сохранён.`);
    } catch {
      setNotice('Не удалось сохранить файл стажировки.');
    }
  };

  return (
    <>
      <Hero
        kicker="SCIENTIFIC INTERNSHIP"
        title="Научная стажировка"
        text="Три последовательных этапа: подготовка → приглашение/приказ/выезд → отчёт и закрытие."
        actions={
          <button type="button" className="dp120-btn gold" onClick={save}>
            Сохранить стажировку
          </button>
        }
      />
      {notice ? <div className="dp120-callout" style={{ marginTop: 10 }}><b>{notice}</b></div> : null}
      <Kpis
        items={[
          ['Нагрузка', '4 ECTS', '120 часов — по текущей странице ENU'],
          ['Минимум', '30 дней', 'Календарных — проверять актуальность'],
          ['До итоговой аттестации', '≥ 2 недели', 'Стажировка должна завершиться заранее'],
          ['Отчёт', '5 дней', 'После окончания — по текущей странице ENU'],
        ]}
      />
      <div className="dp120-grid">
        <Card title="I. Подготовка" sub="Место стажировки должно соответствовать теме и образовательной программе.">
          <div className="dp120-form">
            <Field label="Страна" value={text('country')} onChange={set('country')} />
            <Field label="Организация / университет" value={text('organization')} onChange={set('organization')} />
            <Field label="Принимающий специалист" value={text('supervisor')} onChange={set('supervisor')} />
            <Field label="Планируемые сроки" value={text('planned_dates')} onChange={set('planned_dates')} />
          </div>
          <div style={{ height: 8 }} />
          <DocRow title="План-график научной стажировки" file={form.schedule_file} onPick={pick('schedule_file')} />
          <DocRow title="Предложение / служебное письмо" file={form.request_letter_file} onPick={pick('request_letter_file')} />
        </Card>
        <Card title="II. Приглашение / приказ / выезд" sub="Фиксируем только фактические документы.">
          <DocRow title="Официальное приглашение" file={form.invitation_file} onPick={pick('invitation_file')} />
          <DocRow title="Приказ о направлении на стажировку" file={form.order_file} onPick={pick('order_file')} />
        </Card>
        <Card title="III. После возвращения" sub="Отчёт, сертификат и результаты научного семинара.">
          <DocRow title="Отчёт о научной стажировке" file={form.report_file} onPick={pick('report_file')} />
          <DocRow title="Сертификат / справка о прохождении" file={form.certificate_file} onPick={pick('certificate_file')} />
        </Card>
        <Card title="Актуальные правила ENU" sub="Ссылка на источник, а не постоянный текст.">
          <SourceCard
            title="Научная стажировка ENU"
            text="Текущая страница ENU указывает 4 ECTS / 120 часов, не менее 30 календарных дней и отчётные документы после возвращения. Перед каждым циклом правила перепроверяются."
            links={[
              ['Научная стажировка ENU', OFFICIAL.internship],
              ['Докторантура ENU', OFFICIAL.phd],
            ]}
          />
        </Card>
      </div>
    </>
  );
};
